/**
 * The little bit of IndexedDB this application actually uses.
 *
 * Two stores of key-value records — the save document, and the content cache —
 * read and written whole. That is the entire requirement, and it is smaller
 * than any wrapper library's surface.
 *
 * Written by hand for one reason that matters: **the factory is a parameter**.
 * A wrapper that reaches for `globalThis.indexedDB` internally cannot be handed
 * a fresh database per test, which means tests that look isolated share state
 * and quietly stop proving what they claim — a leaked record from an earlier
 * test is exactly the sort of thing an eviction test would swallow.
 *
 * Every operation resolves rather than rejecting where a failure is survivable.
 * Storage being denied, a quota refusal, or a database deleted from under the
 * tab are all states the game keeps running through; the caller is told by
 * getting nothing back, not by an exception it would have to catch anyway.
 */

/** A promise for a request, with its error swallowed into a rejection. */
function promised<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB request failed'));
    };
  });
}

export interface OpenOptions {
  readonly name: string;
  readonly version: number;
  /** Object stores to create on upgrade, with their key path where they have one. */
  readonly stores: readonly { readonly name: string; readonly keyPath?: string }[];
}

/**
 * Opens the database, or resolves `null` where it cannot be opened at all.
 *
 * `null` rather than a rejection because there is nothing to catch: a browser
 * with storage denied is a supported state, and every caller's answer to it is
 * the same — carry on without persistence.
 */
export function openDatabase(
  factory: IDBFactory,
  options: OpenOptions,
): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(options.name, options.version);
    } catch {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      for (const store of options.stores) {
        if (db.objectStoreNames.contains(store.name)) continue;
        db.createObjectStore(
          store.name,
          store.keyPath === undefined ? {} : { keyPath: store.keyPath },
        );
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // A tab on an old build must not hold a new one hostage: if another
      // context needs to upgrade, this one gets out of the way.
      db.onversionchange = () => {
        db.close();
      };
      resolve(db);
    };
    request.onerror = () => {
      resolve(null);
    };
    request.onblocked = () => {
      resolve(null);
    };
  });
}

/** Runs `body` inside one transaction over `store`, and waits for it to commit. */
export async function transact<T>(
  db: IDBDatabase,
  store: string,
  mode: IDBTransactionMode,
  body: (objectStore: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const transaction = db.transaction(store, mode);
  const settled = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve();
    };
    transaction.onerror = () => {
      reject(transaction.error ?? new Error('IndexedDB transaction failed'));
    };
    transaction.onabort = () => {
      reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    };
  });

  const result = await body(transaction.objectStore(store));
  // Read transactions in some engines never fire `complete` until the
  // microtask queue drains, so the result is awaited first and the commit
  // second — which is also the order that makes a write durable before the
  // caller is told it happened.
  if (mode !== 'readonly') await settled;
  return result;
}

export const get = <T>(store: IDBObjectStore, key: IDBValidKey): Promise<T | undefined> =>
  promised<T | undefined>(store.get(key) as IDBRequest<T | undefined>);

export const getAll = <T>(store: IDBObjectStore): Promise<T[]> =>
  promised<T[]>(store.getAll() as IDBRequest<T[]>);

export const put = (
  store: IDBObjectStore,
  value: unknown,
  key?: IDBValidKey,
): Promise<IDBValidKey> => promised(key === undefined ? store.put(value) : store.put(value, key));

export const remove = (store: IDBObjectStore, key: IDBValidKey): Promise<undefined> =>
  promised(store.delete(key));

export const clearStore = (store: IDBObjectStore): Promise<undefined> => promised(store.clear());
